// features/PhotoEditor/PhotoEditorPage.js - Standalone photo editor page
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FaArrowLeft, FaSpinner } from 'react-icons/fa';
import { Button } from '../../components/ui/button';
import PhotoEditor from './index';
import apiService from '../../services/api';
import { useAppContext } from '../../context/AppContext';
import { useToast } from '../../contexts/ToastContext';
import { getProxiedImageUrl } from '../../utils/helpers';

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

const PhotoEditorPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { imageId } = useParams();
  const { setIsLoading } = useAppContext();
  const { addToast } = useToast();
  
  const [image, setImage] = useState(null);
  const [isLocalLoading, setIsLocalLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Load image if imageId is provided
  useEffect(() => {
    if (!imageId) {
      setIsLocalLoading(false);
      return;
    }
    
    const fetchImage = async () => {
      try {
        setIsLocalLoading(true);
        setIsLoading(true);
        
        // Instead of trying to fetch a specific image by ID (which is not supported by the API),
        // fetch the user's image history and find the image with the matching ID
        const response = await apiService.getUserImageHistory(100, 0);
        
        if (response && response.files && response.files.length > 0) {
          // Find the image with the matching ID
          const targetImage = response.files.find(file => file.id === parseInt(imageId) || file.id === imageId);
          
          if (targetImage && targetImage.url) {
            // Use the proxy for external images to avoid CORS issues
            const proxiedUrl = getProxiedImageUrl(targetImage.url);
            setImage(proxiedUrl);
            setError(null);
          } else {
            throw new Error(t('errors.imageNotFound'));
          }
        } else {
          throw new Error(t('errors.imageNotFound'));
        }
      } catch (error) {
        console.error('Error loading image:', error);
        setError(error.message || t('errors.loadingFailed'));
        addToast({ type: 'error', message: error.message || t('errors.loadingFailed') });
      } finally {
        setIsLocalLoading(false);
        setIsLoading(false);
      }
    };
    
    fetchImage();
  }, [imageId, setIsLoading, addToast, t]);
  
  // Handle saving the edited image
  const handleSave = async (editedImageDataUrl) => {
    try {
      setIsLoading(true);
      
      // Convert data URL to blob
      const response = await fetch(editedImageDataUrl);
      const blob = await response.blob();
      
      // Create a File object from the blob
      const file = new File([blob], 'edited-image.jpg', { type: 'image/jpeg' });
      
      // Save the edited image
      const result = await apiService.saveEditedImage(file, imageId);
      
      if (result && result.success) {
        addToast({ type: 'success', message: t('photoEditor.saveSuccess') });
        
        // Navigate back to image history or to the image details page
        navigate('/image-history');
      } else {
        throw new Error(t('errors.savingFailed'));
      }
    } catch (error) {
      console.error('Error saving edited image:', error);
      addToast({ type: 'error', message: error.message || t('errors.savingFailed') });
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle canceling the edit
  const handleCancel = () => {
    navigate(-1);
  };
  
  // Handle file upload for new image
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const fileURL = URL.createObjectURL(file);
      setImage(fileURL);
    }
  };
  
  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={fadeIn}
      className="container mx-auto px-4 py-6"
    >
      <div className="flex items-center mb-6">
        <Button
          variant="ghost"
          className="mr-4"
          onClick={() => navigate(-1)}
        >
          <FaArrowLeft className="mr-2" />
          {t('common.back')}
        </Button>
        <h1 className="text-2xl font-bold">{t('photoEditor.title')}</h1>
      </div>
      
      {isLocalLoading ? (
        <div className="flex items-center justify-center h-96">
          <FaSpinner className="animate-spin text-4xl text-primary" />
        </div>
      ) : error ? (
        <div className="bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 p-4 rounded-lg">
          <p>{error}</p>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => navigate('/image-history')}
          >
            {t('common.backToGallery')}
          </Button>
        </div>
      ) : image ? (
        <div className="h-[calc(100vh-200px)] min-h-[500px]">
          <PhotoEditor
            initialImage={image}
            onSave={handleSave}
            onCancel={handleCancel}
          />
        </div>
      ) : (
        <div className="text-center p-8 border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-lg">
          <p className="mb-4">{t('photoEditor.noImageSelected')}</p>
          <input
            type="file"
            accept="image/*"
            onChange={handleFileUpload}
            className="hidden"
            id="image-upload"
          />
          <label htmlFor="image-upload" className="inline-block">
            <div className="cursor-pointer">
              <Button
                variant="primary"
                type="button"
                onClick={(e) => {
                  // Prevent the button's onClick from interfering with the label
                  e.preventDefault();
                  document.getElementById('image-upload').click();
                }}
              >
                {t('photoEditor.uploadImage')}
              </Button>
            </div>
          </label>
        </div>
      )}
    </motion.div>
  );
};

export default PhotoEditorPage;
